import { gql } from "@apollo/client";


const LEAD_FIELDS_BASE = `
  id
  businessId
  leadSourceType
  sourceAgentId
  intakeRoutingStatus
  routingMode
  assignedAgentId
  commissionAgentId
  assignmentOrigin
  adminReviewedAt
  adminReviewedByUserId
  business {
    id
    name
    url
    address
    emails
    phoneNumbers {
      countryCode
      phoneNumber
    }
  }
  residentName
  residentKey
  careLevelRequested
  payerType
  urgency
  status
  createdAt
  settlementTotalByReferrer
  extra
  referrerContact { name, email, phone}
  sourceAgentName
  assignedAgentName
  commissionAgentName
  compensationModel
  compensationPercent
  compensationSourceType
  compensationRuleCode
  compensationLockedAt
  compensationNotes
  agentAgreementVersion
  followUpStatus
  lastFollowUpAt
  nextFollowUpAt
  followUpNotes
  attributionSummary {
    leadSourceType
    sourceAgentId
    sourceAgentName
    intakeRoutingStatus
    routingMode
    assignedAgentId
    assignedAgentName
    commissionAgentId
    commissionAgentName
    assignmentOrigin
    adminReviewedAt
    adminReviewedByUserId
  }
  compensationSummary {
    compensationModel
    compensationPercent
    compensationSourceType
    compensationRuleCode
    compensationLockedAt
    compensationNotes
    agentAgreementVersion
    recipientAgentId
    recipientAgentName
    agreementLabel
  }
  followUpSummary {
    followUpStatus
    lastFollowUpAt
    nextFollowUpAt
    followUpNotes
    responsibleAgentId
    responsibleAgentName
  }
`;

const PROVIDER_CONTACT_FIELDS = `
  providerContact {
    status
    emailMasked
    phoneMasked
    email
    phone
    updatedAt
  }
`;

export const GET_BEDS_BY_BUSINESS = gql`
  query BedsByBusinessId($businessId: ID!) {
    bedsByBusinessId(businessId: $businessId) {
      id
      businessId
      roomNumber
      gender
      isOccupied
    }
  }
`;

export const GET_ROOMS_BY_BUSINESS = gql`
  query GetRoomsByBusiness($businessId: Int!) {
    getRoomsByBusiness(businessId: $businessId) {
      id
      businessId
      roomNumber
      roomType
      notes 
      beds {
        id
        bedLabel
        gender
        isOccupied
        notes
        hasPrivateBathroom
        hasHospitalBed
        hasHoyerLift
        hasOxygen
        accommodatesBariatric
      }
    }
  }
`;
// Get a single Room (with beds)
export const GET_ROOM = gql`
  query GetRoom($id: ID!) {
    room(id: $id) {
      id
      businessId
      roomNumber
      roomType
      notes
      beds {
        id
        bedLabel
        gender
        isOccupied
      }
    }
  }
`;

// Get Beds by Room
export const GET_BEDS_BY_ROOM = gql`
  query BedsByRoom($roomId: Int!) {
    bedsByRoom(roomId: $roomId) {
      id
      businessId
      roomId
      bedLabel
      gender
      isOccupied
    }
  }
`;

// Get a single Bed
export const GET_BED = gql`
  query GetBed($id: ID!) {
    bed(id: $id) {
      id
      businessId
      roomId
      bedLabel
      gender
      isOccupied
    }
  }
`;
export const GET_INQUIRIES = gql`
  query GetInquiriesByBusiness($businessId: Int!, $limit: Int, $offset: Int) {
    getInquiriesByBusiness(
      businessId: $businessId
      limit: $limit
      offset: $offset
    ) {
      id
      kind
      name
      email
      phone
      desiredDate
      notes
      extra
      status
      createdAt
      closedAt
    }
  }
`;
export const GET_OPEN_BEDS = gql`
  query GetOpenBeds($businessId: Int!) {
     getRoomsByBusiness(businessId: $businessId) {
      id
      businessId
      roomNumber        
      roomType
      notes 

      beds {
        id
        bedLabel
        gender
        isOccupied
        notes
        hasPrivateBathroom
        hasHospitalBed
        hasHoyerLift
        hasOxygen
        accommodatesBariatric
      }
    }
  }
`;

export const FIND_OPEN_INQUIRY = gql`
  query FindOpenInquiry($businessId: Int!, $email: String, $phone: String) {
    findOpenInquiry(businessId: $businessId, email: $email, phone: $phone) {
      id
      kind
      name
      email
      phone
      desiredDate
      createdAt
    }
  }
`;
export const GET_REQUESTS_BY_CONTACT = gql`
  query GetRequestsByContact(
    $businessId: Int!
    $email: String
    $phone: String
    $limit: Int
    $offset: Int
  ) {
    getRequestsByContact(
      businessId: $businessId
      email: $email
      phone: $phone
      limit: $limit
      offset: $offset
    ) {
      id
      kind
      status
      desiredDate
      createdAt
      notes
      extra
    }
  }
`;

export const GET_REQUESTS_BY_VIEWER = gql`
  query Track($viewerToken: String!) {
    getRequestsByViewer(viewerToken: $viewerToken) {
      id
      kind
      status
      createdAt
      residentName
      intakeNotes # or notes if you only added the alias
      business {
        id
        name
        url
        address
      }
      referrer {
        id
        contactName
        orgName
        email
        phone
      }
    }
  }
`;
export const GET_CARE_LEVELS = gql`
  query GetCareLevels {
    careLevels {
      id
      name
    }
  }
`;

export const GET_REFERRAL_SOURCE_TYPES = gql`
  query GetreferralSourceTypes {
    referralSourceTypes {
      id
      name
    }
  }
`;

export const GET_LATEST_PROVIDER_AGREEMENT_TEMPLATE = gql`
  query GetLatestProviderAgreementTemplate {
    getLatestProviderAgreementTemplate {
      version
      title
      bodyMarkdown
    }
  }
`;

export const GET_PROVIDER_AGREEMENT_STATUS = gql`
  query GetProviderAgreementStatus($businessId: Int!) {
    getProviderAgreementStatus(businessId: $businessId) {
      requiredVersion
      acceptedVersion
      needsAcceptance
    }
  }
`;

export const QUOTE_PLACEMENT = gql`
  query QuotePlacement(
    $businessId: ID!
    $mode: PlacementMode!
    $isRush: Boolean!
  ) {
    quotePlacementPrice(businessId: $businessId, mode: $mode, isRush: $isRush) {
      currency
      baseCents
      rushCents
      totalCents
      mode
      isRush
    }
  }
`;

/* ---------------------------
 * NEW: Quote price from backend using flags in inquiry.extra.pricing
 * -------------------------- */
export const QUOTE_PLACEMENT_PRICE = gql`
  query QuotePlacementPrice(
    $businessId: Int!
    $mode: PlacementMode!
    $isRush: Boolean!
  ) {
    quotePlacementPrice(businessId: $businessId, mode: $mode, isRush: $isRush) {
      currency
      base
      rush
      total
    }
  }
`;

export const BUSINESS_LICENSE = gql`
  query BUSINESS_LICENSE($businessId: ID!) {
    businessLicense(businessId: $businessId) {
      businessId
      claimed
      afhLicenseNumber
      afhLicenseVerified
    }
  }
`;
export const GET_CARE_SETTING = gql`
  query GetCareSetting($businessId: ID!) {
    careSettingByBusinessId(businessId: $businessId) {
      id
      afhLicenseNumber
      afhLicenseVerified
      specialty
      acceptsMedicaid
      hasPrivateRooms
      hasSharedRooms
      acceptsLongTermCareInsurance
      acceptsRespiteCare
      acceptsVeteransBenefits
      acceptsDevelopmentalDisabilities
      acceptsWheelchairAccess
      has24HourCare
      hasRegisteredNurse
      hasVisitingPhysician
      acceptsBedboundResidents
      isPetFriendly
      basePriceMonthly
      genderPreference
      facilityIsCoEd
      privateBathroomAvailable
      hoyerLiftAvailable
      ownersStaffDescription
      specialFeaturesDescription
      philosophyValuesDescription
      dailyLifeDescription
      languagesSpoken
      religionDetails
      houseRules
      includedServicesDetails
      optionalServicesDetails
      mealsDetails
      hygieneDetails
      transportationDetails
      equipmentDetails
      petsAllowed
      catsAllowed
      dogsAllowed
      facilityHasPets
      allowsVapingTobacco
      allowsVapingMedicalMarijuana
      allowsEdibleMedicalMarijuana
      manualWheelchairAllowed
      powerWheelchairAllowed
      powerScooterAllowed
      afhLicenseVerified
      careLevels {
        id
      }
      referralSourceTypes {
        id
      }
    }
  }
`;

export const GET_REFERRAL_LEADS = gql`
  query ReferralLeads(
    $businessId: Int!
    $status: String
    $search: String
    $limit: Int = 100
    $offset: Int = 0
  ) {
    referralLeads(
      businessId: $businessId
      status: $status
      search: $search
      limit: $limit
      offset: $offset
    ) {
      id
      status
      residentName
      residentKey
      careLevelRequested
      payerType
      urgency
      createdAt
      desiredDate
      leadSourceType
      sourceAgentId
      intakeRoutingStatus
      routingMode
      assignedAgentId
      commissionAgentId
      assignmentOrigin
      adminReviewedAt
      adminReviewedByUserId
      sourceAgentName
      assignedAgentName
      commissionAgentName
      attributionSummary {
        leadSourceType
        sourceAgentId
        sourceAgentName
        intakeRoutingStatus
        routingMode
        assignedAgentId
        assignedAgentName
        commissionAgentId
        commissionAgentName
        assignmentOrigin
        adminReviewedAt
        adminReviewedByUserId
      }
      kind
      extra
      settlementTotalByReferrer
      providerContact {
        status
        updatedAt
        emailMasked
        phoneMasked
        email
        phone
      }
      referrer {
        id
        contactName
        orgName
        email
        phone
      }
      referrerContact {
        name
        email
        phone
      }

      referralSourceTypes {
        id
        code
        name
      }
    }
  }
`;

export const GET_REFERRAL_LEAD = gql`
  query ReferralLead($id: ID!) {
    referralLead(id: $id) {
      ${LEAD_FIELDS_BASE}
      ${PROVIDER_CONTACT_FIELDS}
      business {
        id
        name
        url
        cityName
        address
        emails
        phoneNumbers {
          countryCode
          phoneNumber
        }
      }
      referrer {
        id
        contactName
        email
        phone
      }
      sourceAgent {
        id
        username
        email
        phone
        isAgent
      }
      assignedAgent {
        id
        username
        email
        phone
        isAgent
      }
      commissionAgent {
        id
        username
        email
        phone
        isAgent
      }
    }
  }
`;



export const GET_CARE_PRICING_CATALOG = gql`
  query GetCarePricingCatalog {
    carePricingCatalog {
      id
      code # e.g. SELF_SERVE | CONCIERGE | RUSH_SURCHARGE
      label
      amount
      isActive
      updatedAt
    }
  }
`;
export const GET_INSTALLMENTS_BY_LEAD = gql`
  query GetInstallmentsByLead($leadId: ID!) {
    referralInstallments(leadId: $leadId) {
      id
      leadId
      sequence
      dueDate
      dueAmount
      paidAmount
      paidAt
      status # PENDING | PAID | CANCELLED | FAILED
    }
  }
`;
export const GET_REFERRER_BY_TOKEN = gql`
  query ReferrerByToken($token: String!) {
    referrerByPortalToken(token: $token) {
      id
      contactName
      orgName
      email
      phone
      referralLead {
        ${LEAD_FIELDS_BASE}
        ${PROVIDER_CONTACT_FIELDS}
        sourceAgent {
          id
          username
          email
          phone
        }
        assignedAgent {
          id
          username
          email
          phone
        }
        commissionAgent {
          id
          username
          email
          phone
        }
      }
    }
  }
`;

export const GET_MY_REFERRER_PROFILE = gql`
  query MyReferrerProfile {
    myReferrerProfile {
      id
      contactName
      orgName
      email
      phone
      referralLead {
        ${LEAD_FIELDS_BASE}
        ${PROVIDER_CONTACT_FIELDS}
        sourceAgent {
          id
          username
          email
          phone
        }
        assignedAgent {
          id
          username
          email
          phone
        }
        commissionAgent {
          id
          username
          email
          phone
        }
      }
    }
  }
`;

export const CHAT_MESSAGES = gql`
  query ChatMessages($leadId: ID!, $channel: ChatChannel!, $limit: Int) {
    chatMessages(leadId: $leadId, channel: $channel, limit: $limit) {
      id
      leadId
      channel
      senderRole
      body
      createdAt
      __typename
    }
  }
`;
export const GET_REFERRER_READY = gql`
  query GetReferrerReady($bookingTypeId: ID!, $referrerId: ID!) {
    bookingTypeReferrers(
      bookingTypeId: $bookingTypeId
      referrerId: $referrerId
    ) {
      id
      isReady
      referrerId
      bookingTypeId
      groupId
    }
  }
`;

export const BOOKING_TYPES_READY_FOR_REFERRER = gql`
  query BookingTypesReadyForReferrer($businessId: ID!, $referrerId: ID!) {
    bookingTypesReadyForReferrer(
      businessId: $businessId
      referrerId: $referrerId
    ) {
      id
      title
      link
      businessId
      createdByAgentId
    }
  }
`;

export const GET_AGENT_BOOKING_TYPES = gql`
  query GetAgentBookingTypes($businessId: ID!, $createdByAgentId: ID) {
    bookingTypesByBusiness(
      businessId: $businessId
      createdByAgentId: $createdByAgentId
    ) {
      id
      title
      link
      createdByAgentId
    }
  }
`;
export const GET_CONCIERGE_LEADS = gql`
  query GetConciergeLeads(
    $cityCodes: [String!]
    $statuses: [String!]
    $limit: Int
    $offset: Int
  ) {
    getConciergeLeads(
      cityCodes: $cityCodes
      statuses: $statuses
      limit: $limit
      offset: $offset
    ) {
      id
      businessId
      status
      createdAt
      desiredDate
      leadCity
      settlementTotalByReferrer
      leadSourceType
      sourceAgentId
      intakeRoutingStatus
      routingMode
      assignedAgentId
      commissionAgentId
      assignmentOrigin
      adminReviewedAt
      adminReviewedByUserId
      sourceAgentName
      assignedAgentName
      commissionAgentName

      kind
      payerType
      urgency
      careLevelRequested

      business {
        id
        name
        url
        address
        cityName
        emails
        phoneNumbers {
          countryCode
          phoneNumber
        }
      }
      referrerContact {
        id
        name
        email
        phone
      }
      referrer {
        id
        contactName
        email
        phone
      }
      providerContact {
        status
        emailMasked
        phoneMasked
        email
        phone
        updatedAt
      }
      ownerAgentId
      ownerAgentName
      ownerAgent {
        id
        username
        email
        phone
        isAgent
      }
      sourceAgent {
        id
        username
        email
        phone
        isAgent
      }
      assignedAgent {
        id
        username
        email
        phone
        isAgent
      }
      commissionAgent {
        id
        username
        email
        phone
        isAgent
      }
      attributionSummary {
        leadSourceType
        sourceAgentId
        sourceAgentName
        intakeRoutingStatus
        routingMode
        assignedAgentId
        assignedAgentName
        commissionAgentId
        commissionAgentName
        assignmentOrigin
        adminReviewedAt
        adminReviewedByUserId
      }
      compensationSummary {
        compensationModel
        compensationPercent
        compensationSourceType
        compensationRuleCode
        compensationLockedAt
        compensationNotes
        agentAgreementVersion
        recipientAgentId
        recipientAgentName
        agreementLabel
      }
      extra
    }
  }
`;
export const GET_CONCIERGE_BROADCAST_GROUPS = gql`
  query GetConciergeBroadcastGroups($limit: Int, $offset: Int) {
    getConciergeBroadcastGroups(limit: $limit, offset: $offset) {
      id
      winnerLeadId
      closedAt
      createdAt
      leadSourceType
      sourceAgentId
      intakeRoutingStatus
      routingMode
      assignedAgentId
      commissionAgentId
      assignmentOrigin
      adminReviewedAt
      adminReviewedByUserId
      compensationModel
      compensationPercent
      compensationSourceType
      compensationRuleCode
      compensationLockedAt
      compensationNotes
      agentAgreementVersion
      followUpStatus
      lastFollowUpAt
      nextFollowUpAt
      followUpNotes
      ownerAgentId
      ownerAgentName
      sourceAgentName
      assignedAgentName
      commissionAgentName
      ownerAgent {
        id
        username
        email
        phone
        isAgent
      }
      sourceAgent {
        id
        username
        email
        phone
        isAgent
      }
      assignedAgent {
        id
        username
        email
        phone
        isAgent
      }
      commissionAgent {
        id
        username
        email
        phone
        isAgent
      }
      attributionSummary {
        leadSourceType
        sourceAgentId
        sourceAgentName
        intakeRoutingStatus
        routingMode
        assignedAgentId
        assignedAgentName
        commissionAgentId
        commissionAgentName
        assignmentOrigin
        adminReviewedAt
        adminReviewedByUserId
      }
      compensationSummary {
        compensationModel
        compensationPercent
        compensationSourceType
        compensationRuleCode
        compensationLockedAt
        compensationNotes
        agentAgreementVersion
        recipientAgentId
        recipientAgentName
        agreementLabel
      }
      followUpSummary {
        followUpStatus
        lastFollowUpAt
        nextFollowUpAt
        followUpNotes
        responsibleAgentId
        responsibleAgentName
      }
      seedJson
      invites {
        id
        status
        respondedAt
        business {
          id
          name
          cityName
          url
        }
      }
    }
  }
`;
export const GET_REFERRAL_BROADCAST_GROUP = gql`
  query GetReferralBroadcastGroup($id: ID!) {
    referralBroadcastGroup(id: $id) {
      id
      winnerInviteId
      winnerLeadId
      leadSourceType
      sourceAgentId
      intakeRoutingStatus
      routingMode
      assignedAgentId
      commissionAgentId
      assignmentOrigin
      compensationModel
      compensationPercent
      compensationSourceType
      compensationRuleCode
      compensationLockedAt
      compensationNotes
      agentAgreementVersion
      followUpStatus
      lastFollowUpAt
      nextFollowUpAt
      followUpNotes
      sourceAgentName
      assignedAgentName
      commissionAgentName
      attributionSummary {
        leadSourceType
        sourceAgentId
        sourceAgentName
        intakeRoutingStatus
        routingMode
        assignedAgentId
        assignedAgentName
        commissionAgentId
        commissionAgentName
        assignmentOrigin
        adminReviewedAt
        adminReviewedByUserId
      }
      compensationSummary {
        compensationModel
        compensationPercent
        compensationSourceType
        compensationRuleCode
        compensationLockedAt
        compensationNotes
        agentAgreementVersion
        recipientAgentId
        recipientAgentName
        agreementLabel
      }
      followUpSummary {
        followUpStatus
        lastFollowUpAt
        nextFollowUpAt
        followUpNotes
        responsibleAgentId
        responsibleAgentName
      }
      seedJson
      invites {
        id
        businessId
        status
        providerNote
        respondedAt
        actionTokenExpiresAt
        business {
          id
          name
          url
          address
          cityName
          stateCode
          zipcode
          emails
          phoneNumbers {
            phoneNumber
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const SEARCH_BROADCAST_BUSINESSES = gql`
  query SearchBroadcastBusinesses($query: String!) {
    search(query: $query) {
      id
      name
      url
      cityName
      stateCode
      zipcode
      address
      claimStatus
      emails
      phoneNumbers {
        phoneNumber
      }
    }
  }
`;
